/**
 * @fileoverview WebSocket reconnect delay and close-handler helpers.
 * @module pages/chat/websocketReconnect
 */
import { logger } from "../../utils/logger.js";
import {
  WS_RECONNECT_MAX_ATTEMPTS,
  WS_RECONNECT_BASE_DELAY_MS,
  WS_RECONNECT_MAX_DELAY_MS,
} from "../../utils/chatUiConstants.js";

const RECONNECT_BACKOFF_FACTOR = 2;

/** Computes exponential backoff delay capped at the configured maximum. */
export const computeReconnectDelay = (attempt) =>
  Math.min(
    WS_RECONNECT_BASE_DELAY_MS * RECONNECT_BACKOFF_FACTOR ** attempt,
    WS_RECONNECT_MAX_DELAY_MS,
  );

/** Schedules reconnect when the user is active and attempts remain. */
export const handleWebSocketReconnect = (ctx, connectFn) => {
  if (ctx.reconnectTimeoutRef.current) {
    clearTimeout(ctx.reconnectTimeoutRef.current);
  }

  if (!ctx.isUserActiveRef.current) {
    logger.info("User is inactive, not attempting to reconnect");
    return;
  }

  logger.info("User is active, attempting to reconnect...");
  ctx.setIsReconnecting(true);

  const delay = computeReconnectDelay(ctx.reconnectAttemptsRef.current);

  if (ctx.reconnectAttemptsRef.current < WS_RECONNECT_MAX_ATTEMPTS) {
    ctx.reconnectAttemptsRef.current += 1;
    ctx.reconnectTimeoutRef.current = setTimeout(() => {
      if (ctx.ws.current?.readyState === WebSocket.CLOSED) {
        logger.info(
          `Attempting to reconnect WebSocket (attempt ${ctx.reconnectAttemptsRef.current})...`,
        );
        connectFn?.();
      }
    }, delay);
    return;
  }

  logger.info("Max reconnection attempts reached. Please refresh the page.");
  ctx.setIsReconnecting(false);
};
